import { AddVendorFormValues } from "@command-center-app/index";
import { VENDOR_SHIPPING_ADDRESS_TYPE } from "@command-center-app/constants/vendorAddress.const";

export type VendorAddressRecord = {
  type?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  addressLine3?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  zipcode?: string | null;
};

export type VendorAddressFormValues = Pick<
  AddVendorFormValues,
  "addressLine1" | "addressLine2" | "city" | "state" | "country" | "zipcode"
>;

export const getShippingAddress = (
  addresses?: VendorAddressRecord[] | null,
): VendorAddressRecord | undefined => {
  if (!Array.isArray(addresses) || addresses.length === 0) return undefined;

  const shippingAddress = addresses.find(
    (address) =>
      address?.type?.trim().toLowerCase() === VENDOR_SHIPPING_ADDRESS_TYPE,
  );

  return shippingAddress ?? addresses[0];
};

export const getShippingAptSte = (address?: VendorAddressRecord): string => {
  if (!address) return "";
  return (address.addressLine2 || address.addressLine3 || "").trim();
};

export const emptyVendorAddressFormValues = (): VendorAddressFormValues => ({
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  country: "",
  zipcode: "",
});

type VendorAddressSource =
  | VendorAddressRecord[]
  | VendorAddressFormValues
  | null
  | undefined;

export const mapVendorAddressToFormValues = (
  source?: VendorAddressSource,
): VendorAddressFormValues => {
  if (!source) return emptyVendorAddressFormValues();

  if (!Array.isArray(source)) {
    return {
      addressLine1: source.addressLine1?.trim() || "",
      addressLine2: source.addressLine2?.trim() || "",
      city: source.city?.trim() || "",
      state: source.state?.trim() || "",
      country: source.country?.trim() || "",
      zipcode: source.zipcode?.trim() || "",
    };
  }

  const address = getShippingAddress(source);
  if (!address) return emptyVendorAddressFormValues();

  return {
    addressLine1: address.addressLine1?.trim() || "",
    addressLine2: getShippingAptSte(address),
    city: address.city?.trim() || "",
    state: address.state?.trim() || "",
    country: address.country?.trim() || "",
    zipcode: address.zipcode?.trim() || "",
  };
};

export const buildVendorAddressPayload = (values: VendorAddressFormValues) => {
  const payload: Record<string, string> = {};

  const assign = (key: string, value?: string) => {
    if (value !== undefined) {
      payload[key] = value.trim();
    }
  };

  assign("addressLine1", values.addressLine1 ?? "");
  assign("addressLine2", values.addressLine2 ?? "");
  assign("city", values.city ?? "");
  assign("state", values.state ?? "");
  assign("country", values.country ?? "");
  assign("zipcode", values.zipcode ?? "");

  return payload;
};
